// Shared CRM building blocks from the design (components/crm/ui.tsx).
// Only what the converted screens use so far; more are added as screens move over.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUp, Check, ChevronDown, Search, Trash2, Undo2, X } from "lucide-react";

/** The plain white card every CRM surface sits on. */
export const paper = "rounded-lg bg-paper shadow-soft";

/* ---------- Lead status ---------- */

const STATUS_STYLE = {
  New: { chip: "bg-sky-soft text-ink", dot: "bg-sky" },
  Contacted: { chip: "bg-mist text-ink", dot: "bg-sky-deep" },
  Qualified: { chip: "bg-mist text-ink", dot: "bg-ink" },
  "Appointment booked": { chip: "bg-ink text-paper", dot: "bg-sun" },
  Converted: { chip: "bg-success-soft text-success", dot: "bg-success" },
  "Not interested": { chip: "bg-mist text-muted", dot: "bg-muted" },
  Lost: { chip: "bg-danger-soft text-danger", dot: "bg-danger" },
  Junk: { chip: "bg-mist text-muted", dot: "border border-muted bg-paper" },
};
/** The backend's lead status keys → the design's statuses. A clinic's own custom stage keeps its label and the Qualified look. */
const STATUS_FROM_KEY = { new: "New", contacted: "Contacted", qualified: "Qualified", proposal: "Proposal", negotiation: "Negotiation", won: "Converted", lost: "Lost", junk: "Junk" };
export const leadStatusLabel = (key, label) => STATUS_FROM_KEY[key] || label || key || "New";
export const statusDot = (s) => (STATUS_STYLE[s] || STATUS_STYLE.Qualified).dot;

export function StatusBadge({ status, className = "" }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Qualified;
  return (
    <span className={`inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-caption font-semibold ${s.chip} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status}
    </span>
  );
}

/* ---------- People ---------- */

const AVATAR_TONES = ["bg-sky-soft text-ink", "bg-paper text-ink ring-1 ring-line", "bg-ink text-paper", "bg-sky text-ink"];
export function Avatar({ name, initials, size = "md" }) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 997;
  const dims = size === "lg" ? "h-16 w-16 text-h3" : size === "sm" ? "h-8 w-8 text-caption" : "h-10 w-10 text-small";
  return (
    <span aria-hidden className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${dims} ${AVATAR_TONES[h % AVATAR_TONES.length]}`}>
      {initials || "?"}
    </span>
  );
}

/* ---------- Inputs ---------- */

export function TagInput({ id, value, onChange, suggestions = [], placeholder = "Search or create tags", hint = "Type and press Enter to create a new tag" }) {
  const [q, setQ] = useState("");
  const add = (t) => {
    const v = t.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setQ("");
  };
  const rest = suggestions.filter((s) => !value.includes(s) && s.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-sm border border-line bg-paper px-2 py-1.5 focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10">
        {value.map((t) => (
          <span key={t} className="flex h-7 animate-fade-in items-center gap-1 rounded-full bg-mist pl-2.5 pr-1 text-caption font-semibold">
            {t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))} className="flex h-5 w-5 items-center justify-center rounded-full text-muted hover:bg-line hover:text-ink">
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(q);
            } else if (e.key === "Backspace" && !q && value.length) onChange(value.slice(0, -1));
          }}
          placeholder={value.length ? "" : placeholder}
          className="h-7 min-w-32 flex-1 bg-transparent px-1.5 text-small outline-none placeholder:text-muted/70"
        />
      </div>
      {rest.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rest.slice(0, 8).map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="h-7 rounded-full px-2.5 text-caption font-medium text-muted ring-1 ring-line hover:text-ink hover:ring-muted/50">
              + {s}
            </button>
          ))}
        </div>
      )}
      <p className="text-caption text-muted">{hint}</p>
    </div>
  );
}

export function CheckBox({ checked, mixed = false, onChange, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? "mixed" : checked}
      aria-label={label}
      onClick={onChange}
      className={`flex h-5 w-5 items-center justify-center rounded-[5px] ring-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${checked || mixed ? "bg-ink text-paper ring-ink" : "bg-paper ring-muted/50 hover:ring-ink"}`}
    >
      {mixed ? <span className="h-0.5 w-2.5 rounded-full bg-paper" /> : checked && <Check size={13} strokeWidth={2.5} />}
    </button>
  );
}

/**
 * Floating bar for bulk actions, pinned to the bottom centre while rows are selected.
 * Children are the actions; the count and Clear are built in.
 */
export function BulkBar({ count, noun, onClear, children }) {
  if (count === 0) return null;
  return createPortal(
    // `olli`: portalled to <body>, outside the frame, so it carries the design scope itself.
    <div className="olli pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center font-sans">
      <div role="toolbar" aria-label="Bulk actions" className="pointer-events-auto flex h-14 animate-nav-in items-center gap-2 rounded-full bg-ink pl-5 pr-2 text-small text-paper shadow-lift">
        <span className="pr-2 font-semibold tabular-nums">
          {count} {noun}
          {count === 1 ? "" : "s"} selected
        </span>
        <span className="h-6 w-px bg-paper/20" />
        {children}
        <button type="button" onClick={onClear} aria-label="Clear selection" className="flex h-10 w-10 items-center justify-center rounded-full text-paper/70 hover:bg-paper/10 hover:text-paper">
          <X size={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

/* ---------- Lists ---------- */

export function SearchBox({ value, onChange, placeholder, className = "w-72" }) {
  return (
    <label className={`flex h-10 items-center gap-2 rounded-full bg-paper px-4 ring-1 ring-line focus-within:ring-ink/30 ${className}`}>
      <Search size={16} strokeWidth={1.75} className="shrink-0 text-muted" />
      <input value={value} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} placeholder={placeholder} className="h-full min-w-0 flex-1 bg-transparent text-small outline-none placeholder:text-muted" />
    </label>
  );
}

/** Closes a popover on outside click or Escape */
export function useOutside(onClose, active) {
  const ref = useRef(null);
  useEffect(() => {
    if (!active) return;
    const down = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
    };
  }, [active, onClose]);
  return ref;
}

/** Compact filter dropdown with a check on the chosen option. `value === ""` means no filter. */
export function FilterSelect({ label, value, options, onChange, allLabel, renderOption, renderValue }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useOutside(() => { setOpen(false); setQ(""); }, open);
  const active = value !== "";
  const searchable = options.length > 5;
  const shown = q.trim() ? options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase())) : options;
  return (
    <div ref={ref} className="relative">
      <button
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-full pl-4 pr-3 text-small font-medium ring-1 ${active ? "bg-ink text-paper ring-ink" : "bg-paper text-ink ring-line hover:ring-muted/50"}`}
      >
        {/* Label only once a value is picked; "All statuses" already says what it filters */}
        {active && <span className="text-paper/70">{label}</span>}
        {active ? renderValue?.(value) ?? value : allLabel}
        <ChevronDown size={16} strokeWidth={1.75} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-12 z-30 w-64 animate-nav-in rounded-md bg-paper shadow-md ring-1 ring-line" onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); setQ(""); } }}>
        {searchable && (
          <label className="flex h-10 items-center gap-2 border-b border-line px-3">
            <Search size={15} strokeWidth={1.75} className="shrink-0 text-muted" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}`} aria-label={`Search ${label.toLowerCase()}`} className="h-full min-w-0 flex-1 bg-transparent text-small outline-none placeholder:text-muted" />
          </label>
        )}
        <ul role="listbox" aria-label={label} className="max-h-72 overflow-y-auto p-1.5">
          {(q.trim() ? shown : ["", ...shown]).map((o) => (
            <li key={o || "all"}>
              <button
                role="option"
                aria-selected={value === o}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                  setQ("");
                }}
                className={`flex h-9 w-full items-center justify-between rounded-xs px-3 text-left text-small ${value === o ? "bg-mist font-semibold" : "hover:bg-mist"}`}
              >
                {o ? renderOption?.(o) ?? o : allLabel}
                {value === o && <Check size={14} strokeWidth={2} />}
              </button>
            </li>
          ))}
          {q.trim() && !shown.length && <li className="px-3 py-3 text-small text-muted">No matches</li>}
        </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Grid table (design: DataTable). Columns: { id, label, render, sort?, default?, locked?, align?, width? }.
 * `selection` adds a checkbox column; `actions` is pinned to the right edge.
 */
export function DataTable({ columns, rows, rowKey, onOpen, sort, onSort, empty, limit, offset = 0, actions, actionsWidth = "120px", selection }) {
  const template = [...(selection ? ["20px"] : []), ...columns.map((c) => c.width ?? "minmax(140px,1fr)"), ...(actions ? [actionsWidth] : [])].join(" ");
  const allKeys = selection ? rows.map(rowKey) : [];
  const picked = selection ? allKeys.filter((k) => selection.selected.has(k)).length : 0;
  const toggleOne = (k) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    selection.onChange(next);
  };
  // Buttons, links and pickers inside a row act on their own and don't open it
  const fromControl = (e) => !!e.target.closest("button, a, input, select, textarea, [data-row-control]");
  return (
    <div className={`${paper} overflow-hidden`}>
      <div className="overflow-x-auto">
        <div className="min-w-full" style={{ width: "max-content" }}>
          <div role="row" className="grid items-center gap-4 border-b border-line px-6 py-3" style={{ gridTemplateColumns: template }}>
            {selection && (
              <span role="columnheader" className="flex">
                <CheckBox
                  checked={picked > 0 && picked === allKeys.length}
                  mixed={picked > 0 && picked < allKeys.length}
                  label={picked === allKeys.length ? "Clear selection" : `Select all ${allKeys.length} rows`}
                  onChange={() => selection.onChange(picked === allKeys.length ? new Set() : new Set(allKeys))}
                />
              </span>
            )}
            {columns.map((c) => {
              const active = sort?.id === c.id;
              return (
                <span key={c.id} role="columnheader" className={`flex ${c.align === "right" ? "justify-end" : ""}`}>
                  {c.sort ? (
                    <button
                      onClick={() => onSort(active ? (sort.dir === 1 ? { id: c.id, dir: -1 } : null) : { id: c.id, dir: 1 })}
                      className={`flex items-center gap-1 text-caption font-medium ${active ? "text-ink" : "text-muted hover:text-ink"}`}
                    >
                      {c.label}
                      {active && (sort.dir === 1 ? <ArrowUp size={12} strokeWidth={2} /> : <ArrowDown size={12} strokeWidth={2} />)}
                    </button>
                  ) : (
                    <span className="text-caption font-medium text-muted">{c.label}</span>
                  )}
                </span>
              );
            })}
            {actions && <span role="columnheader" className="sticky right-0 -mr-6 bg-paper pr-6 text-right text-caption font-medium text-muted">Actions</span>}
          </div>
          {rows.length === 0 ? null : (
            <ul>
              {rows.slice(offset, offset + limit).map((r, i) => (
                <li
                  key={rowKey(r)}
                  role="row"
                  tabIndex={0}
                  onClick={(e) => !fromControl(e) && onOpen(r)}
                  onKeyDown={(e) => e.key === "Enter" && !fromControl(e) && onOpen(r)}
                  style={{ gridTemplateColumns: template, animationDelay: `${Math.min(i, 14) * 22}ms` }}
                  aria-selected={selection ? selection.selected.has(rowKey(r)) : undefined}
                  className="group/row grid min-h-16 animate-fade-in cursor-pointer items-center gap-4 border-t border-line px-6 py-2.5 outline-none first:border-t-0 hover:bg-mist/60 focus-visible:bg-mist aria-selected:bg-sky-soft/50"
                >
                  {selection && (
                    <span role="cell" className="flex">
                      <CheckBox checked={selection.selected.has(rowKey(r))} label="Select row" onChange={() => toggleOne(rowKey(r))} />
                    </span>
                  )}
                  {columns.map((c) => (
                    <span key={c.id} role="cell" className={`min-w-0 truncate text-small ${c.align === "right" ? "text-right" : ""}`}>
                      {c.render(r)}
                    </span>
                  ))}
                  {actions && (
                    <span role="cell" className="sticky right-0 -mr-6 flex h-full items-center justify-end bg-paper pr-6 group-hover/row:bg-mist">
                      {actions(r)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {rows.length === 0 && empty}
    </div>
  );
}

export function EmptyRow({ title, body, action }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <p className="text-body font-semibold">{title}</p>
      <p className="max-w-sm text-small text-muted">{body}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function UndoToast({ message, onUndo, onDone }) {
  // Callers pass an inline onDone; keep the latest in a ref so a re-render
  // doesn't restart the 6-second timer — only a new message does.
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => done.current(), 6000);
    return () => clearTimeout(t);
  }, [message]);
  return createPortal(
    <div className="olli pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center font-sans" role="status">
      <div className="pointer-events-auto flex h-12 animate-nav-in items-center gap-4 rounded-full bg-ink pl-5 pr-2 text-small font-medium text-paper shadow-lift">
        {message}
        {onUndo && (
          <button
            onClick={() => {
              onUndo();
              onDone();
            }}
            className="flex h-9 items-center gap-1.5 rounded-full bg-paper/10 px-4 font-semibold text-paper hover:bg-paper/20"
          >
            <Undo2 size={15} strokeWidth={1.75} />
            Undo
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

const DANGER_WORDS = /\b(delete|remove|deactivate|disconnect|clear|revoke|cancel appointment)\b/i;
const POSITIVE_WORDS = /\b(launch|schedule|send|publish|post|connect|approve|activate|apply|go live)\b/i;
/** Destructive confirms use red; launching, sending and publishing use the sun primary; anything else is a quiet ink button. */
export const confirmToneFor = (label) => (DANGER_WORDS.test(label) ? "danger" : POSITIVE_WORDS.test(label) ? "positive" : "neutral");

/** Confirm an action. Tone follows the label unless set: red only for destruction, sun for positive actions. */
export function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel, tone }) {
  const t = tone ?? confirmToneFor(confirmLabel);
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onCancel]);
  return createPortal(
    // `olli`: portalled to <body>, outside the frame, so it carries the design scope itself.
    <div className="olli fixed inset-0 z-[70] flex animate-fade-in items-center justify-center bg-ink/35 p-4 font-sans text-ink" onMouseDown={onCancel}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onMouseDown={(e) => e.stopPropagation()} className="flex w-[440px] max-w-full animate-nav-in flex-col gap-5 rounded-xl bg-paper p-7 shadow-lift">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {t === "positive" && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sun-soft text-ink"><Check size={20} strokeWidth={2} /></span>}
            {t === "danger" && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger"><Trash2 size={18} strokeWidth={1.75} /></span>}
            <h2 id="confirm-title" className="pt-1.5 text-h3 font-semibold">
              {title}
            </h2>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <div className="text-small text-muted">{body}</div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="flex h-11 items-center rounded-full px-5 text-small font-semibold hover:bg-mist">
            Cancel
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            className={`flex h-11 items-center gap-2 rounded-full px-5 text-small font-semibold hover:brightness-95 ${t === "danger" ? "bg-danger text-paper" : t === "positive" ? "bg-sun text-ink" : "bg-ink text-paper"}`}
          >
            {t === "danger" && <Trash2 size={16} strokeWidth={1.75} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Priority colours: High red, Medium amber, Low green. */
export const PRIORITY_TONE = {
  High: { bar: "bg-danger", text: "text-danger" },
  Medium: { bar: "bg-warning", text: "text-warning" },
  Low: { bar: "bg-success", text: "text-success" },
};
const priorityTone = (priority) => PRIORITY_TONE[priority] || PRIORITY_TONE.Medium;

/** Priority: bars, like signal strength, in the priority's colour. `inverted` is for a filled background. */
export function PriorityBadge({ priority, inverted = false }) {
  const n = priority === "High" ? 3 : priority === "Medium" ? 2 : 1;
  const tone = priorityTone(priority);
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-small ${inverted ? "" : tone.text}`}>
      <span aria-hidden className="flex items-end gap-[2px]">
        {[1, 2, 3].map((i) => {
          const filled = i <= n;
          return <span key={i} className={`w-[3px] rounded-full ${inverted ? "bg-paper" : filled ? tone.bar : "bg-line"}`} style={{ height: 4 + i * 3, opacity: inverted && !filled ? 0.35 : undefined }} />;
        })}
      </span>
      {priority}
    </span>
  );
}

/** The coloured edge of a task row. A finished task keeps a plain one, so only open work stands out. */
export function PriorityStripe({ priority, done = false }) {
  return <span role="img" aria-label={`${priority} priority`} title={`${priority} priority`} className={`w-1 shrink-0 self-stretch rounded-full ${done ? "bg-line" : priorityTone(priority).bar}`} />;
}
